import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppButton from "../button/AppButton";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { useTranslation } from "../../translations/i18n";
import { formatLongDisplayDate } from "../../utils/helperFunctions/dateFormatter";

interface BookingDetailsModalProps {
  open: boolean;
  booking: BookingResponse | null;
  loading: boolean;
  canManage?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onCancelBooking: () => void;
  onPaymentStatusChange: (status: BookingResponse["payment_status"]) => void;
  onSendInvoice: () => void;
}

const normalizePhoneForWhatsApp = (phone: string) => {
  const normalized = phone.trim();

  if (normalized.startsWith("+")) {
    return normalized.replace(/\D/g, "");
  }

  if (normalized.startsWith("00")) {
    return normalized.slice(2).replace(/\D/g, "");
  }

  return normalized.replace(/\D/g, "");
};

const BookingDetailsModal = ({
  open,
  booking,
  loading,
  canManage = true,
  onClose,
  onEdit,
  onCancelBooking,
  onPaymentStatusChange,
  onSendInvoice,
}: BookingDetailsModalProps) => {
  const { t } = useTranslation();

  const [contactOpen, setContactOpen] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);

  const whatsAppTemplates = useMemo(() => {
    if (!booking) {
      return [];
    }

    const guestName =
      booking.guest?.first_name || booking.guest?.full_name || "";
    const unitName = booking.unit?.name || "";
    const checkIn = booking.check_in;
    const checkOut = booking.check_out;

    return [
      t("bookingMessages.templateOne", {
        guest: guestName,
        unit: unitName,
        checkIn,
        checkOut,
      }),
      t("bookingMessages.templateTwo", {
        guest: guestName,
        unit: unitName,
        checkIn,
        checkOut,
      }),
      t("bookingMessages.templateThree", {
        guest: guestName,
        unit: unitName,
        checkIn,
        checkOut,
      }),
    ];
  }, [booking, t]);

  const [whatsAppMessage, setWhatsAppMessage] = useState("");

  if (!booking) {
    return null;
  }

  const hasEmail = Boolean(booking.guest?.email);
  const hasPhone = Boolean(booking.guest?.phone);
  const canCancel = booking.status !== "cancelled";

  const openEmail = () => {
    if (!booking.guest?.email) {
      window.alert(t("booking.missingEmail"));
      return;
    }

    const subject = encodeURIComponent(
      `RentPilot - ${booking.unit?.name || ""}`,
    );
    const body = encodeURIComponent(
      `${t("booking.checkIn")}: ${booking.check_in}\n${t(
        "booking.checkOut",
      )}: ${booking.check_out}\n${t("booking.total")}: ${Number(
        booking.total_price,
      ).toFixed(2)} ${booking.currency}`,
    );

    window.location.href = `mailto:${booking.guest.email}?subject=${subject}&body=${body}`;
  };

  const openWhatsAppMessageModal = () => {
    if (!booking.guest?.phone) {
      window.alert(t("booking.missingPhone"));
      return;
    }

    setWhatsAppMessage(whatsAppTemplates[0] || "");
    setContactOpen(false);
    setWhatsAppOpen(true);
  };

  const openWhatsApp = () => {
    if (!booking.guest?.phone || !whatsAppMessage.trim()) {
      return;
    }

    const phone = normalizePhoneForWhatsApp(booking.guest.phone);
    const text = encodeURIComponent(whatsAppMessage.trim());

    window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener");
    setWhatsAppOpen(false);
  };

  const confirmSendInvoice = () => {
    if (!booking.guest?.email) {
      window.alert(t("booking.missingEmail"));
      return;
    }

    if (!window.confirm(t("booking.confirmSendInvoice"))) {
      return;
    }

    setContactOpen(false);
    onSendInvoice();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Box>{t("booking.bookingDetails")}</Box>

            {canManage ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setContactOpen(true)}
                disabled={loading}
              >
                {t("booking.contactGuest")}
              </Button>
            ) : null}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {booking.guest?.full_name || t("booking.guest")}
              </Typography>

              <Typography color="text.secondary">
                {booking.unit?.property?.name
                  ? `${booking.unit.property.name} · `
                  : ""}
                {booking.unit?.name || t("booking.unit")}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip
                label={t(
                  `booking.${
                    booking.status === "checked_in"
                      ? "checkedIn"
                      : booking.status === "checked_out"
                        ? "checkedOut"
                        : booking.status
                  }`,
                )}
              />

              <Chip
                label={t(`booking.${booking.payment_status}`)}
                color="primary"
              />
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Typography>
                {t("booking.checkIn")}:{" "}
                {formatLongDisplayDate(booking.check_in)}
              </Typography>
              <Typography>
                {t("booking.checkOut")}:{" "}
                {formatLongDisplayDate(booking.check_out)}
              </Typography>
              <Typography>
                {t("booking.nights")}: {booking.nights}
              </Typography>
              <Typography>
                {t("booking.numberOfGuests")}: {booking.guest_count || "-"}
              </Typography>
              <Typography>
                {t("booking.total")}: {Number(booking.total_price).toFixed(2)}{" "}
                {booking.currency}
              </Typography>

              {booking.guest?.phone ? (
                <Typography>
                  {t("booking.phone")}: {booking.guest.phone}
                </Typography>
              ) : null}

              {booking.guest?.email ? (
                <Typography>
                  {t("booking.email")}: {booking.guest.email}
                </Typography>
              ) : null}

              {booking.notes ? (
                <Typography>
                  {t("booking.notes")}: {booking.notes}
                </Typography>
              ) : null}
            </Stack>

            {canManage ? (
              <>
                <Divider />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <AppButton
                    title={t("booking.unpaid")}
                    variant={
                      booking.payment_status === "unpaid"
                        ? "contained"
                        : "outlined"
                    }
                    fullWidth={false}
                    disabled={loading || !canCancel}
                    onClick={() => onPaymentStatusChange("unpaid")}
                  />

                  <AppButton
                    title={t("booking.deposit")}
                    variant={
                      booking.payment_status === "deposit"
                        ? "contained"
                        : "outlined"
                    }
                    fullWidth={false}
                    disabled={loading || !canCancel}
                    onClick={() => onPaymentStatusChange("deposit")}
                  />

                  <AppButton
                    title={t("booking.paid")}
                    variant={
                      booking.payment_status === "paid"
                        ? "contained"
                        : "outlined"
                    }
                    fullWidth={false}
                    disabled={loading || !canCancel}
                    onClick={() => onPaymentStatusChange("paid")}
                  />
                </Stack>
              </>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1 }}>
          <AppButton
            title={t("common.close")}
            variant="outlined"
            color="inherit"
            fullWidth={false}
            disabled={loading}
            onClick={onClose}
          />

          {canManage ? (
            <>
              <AppButton
                title={t("common.edit")}
                fullWidth={false}
                disabled={loading || !canCancel}
                onClick={onEdit}
              />

              {canCancel ? (
                <AppButton
                  title={t("booking.cancelBooking")}
                  color="error"
                  variant="outlined"
                  fullWidth={false}
                  disabled={loading}
                  onClick={onCancelBooking}
                />
              ) : null}
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t("booking.contactGuest")}
        </DialogTitle>

        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {booking.guest?.full_name || "-"}
          </Typography>

          {!hasEmail && !hasPhone ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("booking.noContactInfo")}
            </Alert>
          ) : null}

          <Stack spacing={1}>
            <Button
              variant="outlined"
              disabled={!hasEmail || loading || !canCancel}
              onClick={confirmSendInvoice}
            >
              {t("booking.invoice")}
            </Button>

            <Button variant="outlined" disabled={!hasEmail} onClick={openEmail}>
              {t("booking.email")}
            </Button>

            <Button
              variant="outlined"
              disabled={!hasPhone}
              onClick={openWhatsAppMessageModal}
            >
              {t("booking.whatsapp")}
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={whatsAppOpen}
        onClose={() => setWhatsAppOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t("bookingMessages.title")}
        </DialogTitle>

        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {booking.guest?.full_name || "-"} · {booking.guest?.phone || "-"}
          </Typography>

          <Stack spacing={1} sx={{ mb: 2 }}>
            {whatsAppTemplates.map((template, index) => (
              <Button
                key={`${template}-${index}`}
                variant={
                  whatsAppMessage === template ? "contained" : "outlined"
                }
                onClick={() => setWhatsAppMessage(template)}
              >
                {t("bookingMessages.templateButton", { number: index + 1 })}
              </Button>
            ))}
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={5}
            value={whatsAppMessage}
            onChange={(event) => setWhatsAppMessage(event.target.value)}
            placeholder={t("bookingMessages.customPlaceholder")}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setWhatsAppOpen(false)}>
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={openWhatsApp}
            disabled={!whatsAppMessage.trim()}
          >
            {t("bookingMessages.openWhatsApp")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingDetailsModal;
